/**
 * Data labels transform.
 *
 * Adds value labels on top of data points when `showLabels` is enabled.
 * Handles label position, percent mode (pie/funnel), abbreviation,
 * and scatter array-value extraction.
 */
import type { EChartsOption } from 'echarts';
import { formatChartValue } from '../../../../../utils/formatters';
import type { ChartStyleState } from '../../../../edit/visualConfig';
import { getDefaultLabelPosition } from '../../../../edit/visualConfig';

/**
 * Resolve the effective ECharts label position.
 *
 * Uses the configured position directly. Legacy `'auto'` values (from
 * saved charts before the config-driven migration) are resolved to the
 * chart-type default via `getDefaultLabelPosition`.
 *
 * Pie/funnel only support 'inside' | 'outside'.
 */
function resolveLabelPosition(pos: string, seriesType: string): string {
  // Map legacy 'top' and 'auto' values to chart-type default
  const effective = (pos === 'auto' || pos === 'top' || !pos)
    ? getDefaultLabelPosition(seriesType)
    : pos;

  // Pie/funnel only support inside/outside
  if (seriesType === 'pie' || seriesType === 'funnel') {
    return effective === 'inside' ? 'inside' : 'outside';
  }
  return effective;
}

/** Build a label formatter based on chart type and visual config flags. */
function buildFormatter(
  seriesType: string,
  vc: ChartStyleState,
): string | ((params: any) => string) {
  // Pie / funnel: always include category name in label
  if (seriesType === 'pie' || seriesType === 'funnel') {
    if (vc.showPercent) return '{b}: {d}%';
    return (params: any) => {
      const raw = params.value ?? params.data;
      const v = typeof raw === 'number' ? raw : (raw?.value ?? raw);
      const num = typeof v === 'number' ? v : Number(v);
      if (isNaN(num)) return params.name ?? '';
      const formatted = vc.abbreviateNumbers ? formatChartValue(num) : (num % 1 === 0 ? String(num) : parseFloat(num.toFixed(2)).toString());
      return `${params.name}: ${formatted}`;
    };
  }

  return (params: any) => {
    const raw = params.value ?? params.data;
    // Scatter data is [x, y, size?] — display the Y value
    const v = Array.isArray(raw) ? raw[1] : raw;
    const num = typeof v === 'number' ? v : Number(v);
    if (isNaN(num)) return String(v ?? '');
    if (vc.abbreviateNumbers) return formatChartValue(num);
    return num % 1 === 0 ? String(num) : parseFloat(num.toFixed(2)).toString();
  };
}

export function applyLabels(option: EChartsOption, vc: ChartStyleState): EChartsOption {
  if (!Array.isArray(option.series)) return option;

  // Pie/funnel always have visible labels (category names) — apply position
  // even when showLabels is off.  For other chart types, skip entirely.
  if (!vc.showLabels && !vc.showPercent) {
    const result = { ...option };
    result.series = (result.series as any[]).map((s: any) => {
      const isPie = s.type === 'pie' || s.type === 'funnel';
      if (!isPie) return s;
      const pos = resolveLabelPosition(vc.labelPosition, s.type);
      return {
        ...s,
        label: {
          ...s.label,
          show: true,
          position: pos,
          formatter: '{b}', // Name only — no value or percent
          ...(pos !== 'inside' ? { color: 'inherit' } : {}),
        },
        labelLine: { ...s.labelLine, show: pos !== 'inside' },
      };
    });
    return result;
  }

  // Percent-only mode (showPercent without showLabels): show name + percent
  if (!vc.showLabels && vc.showPercent) {
    const result = { ...option };
    result.series = (result.series as any[]).map((s: any) => {
      const isPie = s.type === 'pie' || s.type === 'funnel';
      if (!isPie) return s;
      const pos = resolveLabelPosition(vc.labelPosition, s.type);
      return {
        ...s,
        label: {
          ...s.label,
          show: true,
          position: pos,
          formatter: '{b}: {d}%',
          ...(pos !== 'inside' ? { color: 'inherit' } : {}),
        },
        labelLine: { ...s.labelLine, show: pos !== 'inside' },
      };
    });
    return result;
  }

  const result = { ...option };
  result.series = (result.series as any[]).map((s: any) => {
    const isPie = s.type === 'pie' || s.type === 'funnel';
    const pos = resolveLabelPosition(vc.labelPosition, s.type);
    const patched: any = {
      ...s,
      label: {
        ...s.label,
        show: true,
        position: pos,
        formatter: buildFormatter(s.type, vc),
        // Pie/funnel outside labels: use inherit color so text is visible
        // against the chart background (not white-on-white)
        ...(isPie && pos !== 'inside' ? { color: 'inherit' } : {}),
      },
    };
    // Pie/funnel: always show label connecting lines when labels are visible
    if (isPie) {
      patched.labelLine = { ...s.labelLine, show: pos !== 'inside' };
    }
    // ECharts line series only render labels at symbol positions.
    // When symbols are hidden (area default), force invisible symbols
    // so labels still have anchor points.
    if (s.type === 'line' && s.showSymbol === false) {
      patched.showSymbol = true;
      patched.symbolSize = 0;
    }
    return patched;
  });

  return result;
}
